import { apiClient } from './api';

/**
 * Legal & Compliance Dashboard service (VIS-151).
 *
 * Frontend-orchestrated aggregation: this service fans out to the EXISTING
 * L&C API-Gateway endpoints (all under `/api/v1/legal/*`, already gated to the
 * `legal` module) and shapes the responses into a single dashboard view model.
 * It adds NO backend and calls NO Corporate Security endpoints — every URL in
 * {@link LC_DASHBOARD_ENDPOINTS} is an L&C path (asserted by a unit test).
 *
 * Resilience: each source is fetched independently. A source that errors or
 * returns an unsuccessful envelope degrades to zero/empty and its name is added
 * to `failedSources` so the page can show a non-blocking warning rather than
 * blanking the whole dashboard.
 */

/** Every endpoint this service may call. Used by the page and guarded by a test. */
export const LC_DASHBOARD_ENDPOINTS = {
  vendorAnalytics: '/api/v1/legal/reporting/analytics',
  mattersSummary: '/api/v1/legal/matters/reporting/summary',
  mattersUpcoming: '/api/v1/legal/matters/reporting/upcoming-dates?days=30',
  hipaaDashboard: '/api/v1/legal/hipaa/dashboard',
  trackingDashboard: '/api/v1/legal/tracking/dashboard',
  businessEntities: '/api/v1/legal/hipaa/business-entities?limit=1',
  recentMatters: '/api/v1/legal/matters?limit=5&sortBy=createdAt&sortOrder=desc',
  recentHipaa: '/api/v1/legal/hipaa?limit=5',
} as const;

export type LegalDashboardSource = keyof typeof LC_DASHBOARD_ENDPOINTS;

export interface LegalHeadlineCounts {
  vendors: number;
  contracts: number;
  matters: number;
  hipaaIncidents: number;
  trackingRequests: number;
  businessEntities: number;
}

export type AttentionSeverity = 'high' | 'medium' | 'low';

export interface LegalAttentionItem {
  key: string;
  label: string;
  count: number;
  severity: AttentionSeverity;
  /** L&C list route to drill through to. */
  href: string;
}

export interface LegalActivityItem {
  id: string;
  type: 'matter' | 'hipaa';
  title: string;
  description: string;
  /** ISO timestamp; used only for sorting. */
  timestamp: string;
}

export interface LegalDashboardData {
  headline: LegalHeadlineCounts;
  attention: LegalAttentionItem[];
  activity: LegalActivityItem[];
  /** Source keys that failed to load (page surfaces a non-blocking warning). */
  failedSources: LegalDashboardSource[];
}

// ── Minimal response shapes (only the fields we read) ──────────────────────

interface VendorAnalyticsShape {
  vendors?: { total?: number };
  contracts?: { total?: number; expiringIn30Days?: number; expiringIn90Days?: number };
  obligations?: { overdueCount?: number };
}

interface MattersSummaryShape {
  byStatus?: Array<{ status: string; count: number }>;
}

type MattersUpcomingShape = Array<{ id: string }>;

interface HipaaDashboardShape {
  widgets?: {
    openIncidents?: { total?: number };
    notificationDeadlines?: { tierCounts?: Partial<Record<string, number>> };
  };
}

interface TrackingDashboardShape {
  openCount?: number;
  overdueCount?: number;
}

interface ListEnvelopeMeta {
  pagination?: { total?: number };
}

interface RecentMattersShape {
  matters?: Array<{
    id: string;
    matterNumber?: string;
    title?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
}

interface RecentHipaaShape {
  items?: Array<{
    id: string;
    incidentNumber?: string;
    status?: string;
    description?: string;
    createdAt?: string;
    reportingDate?: string;
  }>;
}

function toNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

class LegalDashboardService {
  /**
   * Fetch a single source, returning `null` on any failure. Tracks the source
   * key in `failed` so the caller can report partial outages.
   */
  private async safeGet<T>(
    url: string,
    key: LegalDashboardSource,
    failed: LegalDashboardSource[]
  ): Promise<{ data?: T; meta?: ListEnvelopeMeta } | null> {
    try {
      const res = await apiClient.get<T>(url);
      if (!res.success) {
        failed.push(key);
        return null;
      }
      return { data: res.data, meta: res.meta as ListEnvelopeMeta | undefined };
    } catch {
      failed.push(key);
      return null;
    }
  }

  async getDashboard(): Promise<LegalDashboardData> {
    const failedSources: LegalDashboardSource[] = [];

    const [
      vendorAnalytics,
      mattersSummary,
      mattersUpcoming,
      hipaa,
      tracking,
      businessEntities,
      recentMatters,
      recentHipaa,
    ] = await Promise.all([
      this.safeGet<VendorAnalyticsShape>(
        LC_DASHBOARD_ENDPOINTS.vendorAnalytics,
        'vendorAnalytics',
        failedSources
      ),
      this.safeGet<MattersSummaryShape>(
        LC_DASHBOARD_ENDPOINTS.mattersSummary,
        'mattersSummary',
        failedSources
      ),
      this.safeGet<MattersUpcomingShape>(
        LC_DASHBOARD_ENDPOINTS.mattersUpcoming,
        'mattersUpcoming',
        failedSources
      ),
      this.safeGet<HipaaDashboardShape>(
        LC_DASHBOARD_ENDPOINTS.hipaaDashboard,
        'hipaaDashboard',
        failedSources
      ),
      this.safeGet<TrackingDashboardShape>(
        LC_DASHBOARD_ENDPOINTS.trackingDashboard,
        'trackingDashboard',
        failedSources
      ),
      this.safeGet<{ items?: unknown[] }>(
        LC_DASHBOARD_ENDPOINTS.businessEntities,
        'businessEntities',
        failedSources
      ),
      this.safeGet<RecentMattersShape>(
        LC_DASHBOARD_ENDPOINTS.recentMatters,
        'recentMatters',
        failedSources
      ),
      this.safeGet<RecentHipaaShape>(
        LC_DASHBOARD_ENDPOINTS.recentHipaa,
        'recentHipaa',
        failedSources
      ),
    ]);

    const va = vendorAnalytics?.data;
    const mattersByStatus = mattersSummary?.data?.byStatus ?? [];
    const openMatters = mattersByStatus.reduce((sum, s) => sum + toNumber(s.count), 0);
    const upcomingCount = Array.isArray(mattersUpcoming?.data) ? mattersUpcoming.data.length : 0;
    const tierCounts = hipaa?.data?.widgets?.notificationDeadlines?.tierCounts ?? {};
    const trackingData = tracking?.data;

    const headline: LegalHeadlineCounts = {
      vendors: toNumber(va?.vendors?.total),
      contracts: toNumber(va?.contracts?.total),
      matters: openMatters,
      hipaaIncidents: toNumber(hipaa?.data?.widgets?.openIncidents?.total),
      trackingRequests: toNumber(trackingData?.openCount),
      businessEntities: toNumber(businessEntities?.meta?.pagination?.total),
    };

    const attention: LegalAttentionItem[] = [
      {
        key: 'overdue-obligations',
        label: 'Overdue contract obligations',
        count: toNumber(va?.obligations?.overdueCount),
        severity: 'high',
        href: '/legal/contracts',
      },
      {
        key: 'hipaa-deadlines',
        label: 'HIPAA notifications due soon / overdue',
        count: toNumber(tierCounts.RED) + toNumber(tierCounts.AMBER),
        severity: 'high',
        href: '/legal/hipaa',
      },
      {
        key: 'overdue-tracking',
        label: 'Overdue document tracking requests',
        count: toNumber(trackingData?.overdueCount),
        severity: 'high',
        href: '/legal/tracking',
      },
      {
        key: 'expiring-contracts',
        label: 'Contracts expiring within 30 days',
        count: toNumber(va?.contracts?.expiringIn30Days),
        severity: 'medium',
        href: '/legal/vendors/auto-renewals',
      },
      {
        key: 'matter-key-dates',
        label: 'Matters with key dates within 30 days',
        count: upcomingCount,
        severity: 'medium',
        href: '/legal/matters',
      },
    ];

    const activity = this.buildActivity(recentMatters?.data, recentHipaa?.data);

    return { headline, attention, activity, failedSources };
  }

  private buildActivity(
    matters: RecentMattersShape | undefined,
    hipaa: RecentHipaaShape | undefined
  ): LegalActivityItem[] {
    const items: LegalActivityItem[] = [];

    for (const m of matters?.matters ?? []) {
      items.push({
        id: `matter-${m.id}`,
        type: 'matter',
        title: m.title ? `${m.matterNumber ?? 'Matter'} — ${m.title}` : (m.matterNumber ?? 'Legal matter'),
        description: `Legal matter${m.status ? ` · ${m.status.replace(/_/g, ' ')}` : ''}`,
        timestamp: m.updatedAt ?? m.createdAt ?? '',
      });
    }

    for (const h of hipaa?.items ?? []) {
      items.push({
        id: `hipaa-${h.id}`,
        type: 'hipaa',
        title: h.incidentNumber ?? 'HIPAA incident',
        description: `HIPAA incident${h.status ? ` · ${h.status.replace(/_/g, ' ')}` : ''}`,
        timestamp: h.createdAt ?? h.reportingDate ?? '',
      });
    }

    return items
      .sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''))
      .slice(0, 10);
  }
}

export const legalDashboardService = new LegalDashboardService();
